// Documents service for API calls
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const documentsService = {
  // Get property documents
  getPropertyDocuments: async (propertyId, page = 1, perPage = 10) => {
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/documents?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching property documents:', error);
      throw error;
    }
  },

  // Get property certificates
  getPropertyCertificates: async (propertyId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/certificates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching property certificates:', error);
      throw error;
    }
  },

  // Get single document
  getPropertyDocument: async (documentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/documents/${documentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  },

  // Get single ticket document
  getTicketDocument: async (ticketId, documentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}/documents/${documentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching ticket document:', error);
      throw error;
    }
  },

  // Update document
  updatePropertyDocument: async (documentId, formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  },

  // Delete document
  deletePropertyDocument: async (propertyId, documentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  // Download document
  downloadPropertyDocument: async (propertyId, documentId) => {
    try {
      // ✅ FIXED: Backend now redirects to presigned URL, so we need to follow the redirect
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/documents/${documentId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        redirect: 'follow', // Follow redirects to presigned URL
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Backend redirects to presigned URL, so response.url contains the final presigned URL
      // Return the presigned URL for frontend to use
      return response.url; // This is the presigned URL after redirect
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  },

  // Create document
  createPropertyDocument: async (propertyId, formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData, // FormData will automatically set the correct Content-Type
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  },

  // Get document types
  getDocumentTypes: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/types`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching document types:', error);
      throw error;
    }
  },

  // Ticket Document Methods
  getTicket: async (ticketId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  },

  createTicketDocument: async (ticketId, formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData, // FormData will automatically set the correct Content-Type
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating ticket document:', error);
      throw error;
    }
  },

  getTicketDocuments: async (ticketId, page = 1, perPage = 10) => {
    try {
      const params = new URLSearchParams({ page, per_page: perPage });
      const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}/documents?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching ticket documents:', error);
      throw error;
    }
  },

  updateTicketDocument: async (ticketId, documentId, formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating ticket document:', error);
      throw error;
    }
  },

  deleteTicketDocument: async (ticketId, documentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting ticket document:', error);
      throw error;
    }
  },

  downloadTicketDocument: async (ticketId, documentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${ticketId}/documents/${documentId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Error downloading ticket document:', error);
      throw error;
    }
  }
};

export default documentsService; 